import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer -- the Spotlight is controlled by Engagement Platform's coordination client
import { SpotlightTransition, Spotlight } from '@atlaskit/onboarding';

import { useCoordinationClient } from '@confluence/engagement-provider';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

export interface FirstDatabaseGAOnboardingProps {
	currentStep?: string;
	setNextStep?: (step?: string) => void;
}
const HELP_SPOTLIGHT_STEP = 'help-spotlight';

const i18n = defineMessages({
	body: {
		id: 'databases-onboarding-ga.getting-started.content.spotlight.help.body',
		defaultMessage: 'Get help or learn more about databases anytime.',
		description: "Body text that let's the user know where to get help.",
	},
	dismissBtn: {
		id: 'databases-onboarding-ga.getting-started.content.spotlight.help.dismissBtn',
		defaultMessage: 'OK',
		description: 'Dismiss button for help spotlight.',
	},
});

const SCREEN_NAME = 'databaseGAOnboardingHelpSpotlight';
const MESSAGE_ID = 'cc-databases-ga-modify-db-spotlight';

export const FirstDatabaseGAOnboarding = ({
	currentStep,
	setNextStep = () => {},
}: FirstDatabaseGAOnboardingProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const showHelpSpotlight = currentStep === HELP_SPOTLIGHT_STEP;

	const coordinationClient = useCoordinationClient();

	useEffect(() => {
		if (showHelpSpotlight) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: SCREEN_NAME,
				},
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep]);

	const handleDismiss = useCallback(async () => {
		setNextStep();
		try {
			await coordinationClient.stop(MESSAGE_ID);
		} catch (err) {
			getMonitoringClient().submitError(err, {
				attribution: Attribution.CONTENT_TYPES,
			});
		}
	}, [setNextStep, coordinationClient]);

	return (
		<>
			<SpotlightTransition>
				{showHelpSpotlight && (
					<Spotlight
						dialogWidth={287}
						targetRadius={100}
						target="app-navigation-help"
						key="database-ga-onboarding"
						dialogPlacement="top right"
						actions={[
							{
								text: intl.formatMessage(i18n.dismissBtn),
								onClick: handleDismiss,
							},
						]}
					>
						<FormattedMessage {...i18n.body} />
					</Spotlight>
				)}
			</SpotlightTransition>
		</>
	);
};
